import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { faBuilding, faUsers, faUserShield, faUserTie } from '@fortawesome/free-solid-svg-icons'
import { useNotify } from '@globaleris/ui.library'
import { Box, Chip, useTheme } from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { AtlasAccessKpiModel } from '../../../api/Argentia/endpoints/atlasAccess/model/atlasAccess'
import Page, { PageLayout } from '../../../components/page/page'
import PageCenterColumn from '../../../components/page/pageCenterColumn'
import PageHeader from '../../../components/page/pageHeader'
import { useConfirmationDialog } from '../../../hooks'
import { ConfirmationDialogs } from '../../../model/enums/confirmationDialogs'
import { AtlasAccessFormSearchFilter } from './atlasAccess.models'
import {
  deactivateAtlasUser,
  fetchAtlasAccessKpis,
  fetchAtlasAccessTable,
  fetchAtlasSuperUsers,
  removeAtlasSuperUserPermission,
  searchAtlasAccess
} from './atlasAccess.service'
import { AddSuperUserDialog } from './components/addSuperUserDialog/addSuperUserDialog'
import { ClientDetailAccessDialog } from './components/clientDetailAccessDialog/clientDetailAccessDialog'
import {
  ClientAccessModel,
  ClientsAccessTable
} from './components/clientsAccessTable/clientsAccessTable'
import KpiItem from './components/kpiItem/kpiItem'
import { SearchFilterCard } from './components/searchFilterCard/searchFilterCard'
import { SuperUsersCard, SuperUserTableModel } from './components/superUsersCard/superUsersCard'

interface AtlasAccessPageProps {
  location?: {
    state: {
      pageProp?: string
    }
  }
}

export const AtlasAccessPage: React.FC<AtlasAccessPageProps> = props => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'atlasAccess'])
  const history = useHistory()
  const theme = useTheme()
  const { error, info } = useNotify()
  const { getConfirmation, closeConfirmation } = useConfirmationDialog()

  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [isSearchOpen, setIsSearchOpen] = useState<boolean>(false)
  const [atlasAccessKpis, setAtlasAccessKpis] = useState<AtlasAccessKpiModel | undefined>(undefined)

  const [clientsWithAccess, setClientsWithAccess] = useState<ClientAccessModel[]>([])
  // listado completo sin filtro (GetAtlasAccessTable) - al cual vuelve "Limpiar" sin re-consultar
  const [allClientsWithAccess, setAllClientsWithAccess] = useState<ClientAccessModel[]>([])
  const [isFetchingClients, setIsFetchingClients] = useState<boolean>(false)

  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState<boolean>(false)
  const [selectedClientAccess, setSelectedClientAccess] = useState<ClientAccessModel | undefined>(
    undefined
  )

  const [superUsers, setSuperUsers] = useState<SuperUserTableModel[]>([])
  const [isFetchingSuperUsers, setIsFetchingSuperUsers] = useState<boolean>(false)
  const [isAddSuperUserDialogOpen, setIsAddSuperUserDialogOpen] = useState<boolean>(false)

  // #region Handlers Starts

  // ------------------- Header Action Buttons Handlers -----------------------------//

  const handleBackButtonClick = () => {
    history.goBack()
  }

  const handleSave = async () => {
    // Save Logic
  }

  const handleToggleSearch = () => {
    setIsSearchOpen(prev => !prev)
  }

  // #endregion Handlers Ends

  // #region Functions Starts
  const handleViewClientAccessDetail = (row: ClientAccessModel) => {
    setSelectedClientAccess(row)
    setIsDetailDialogOpen(true)
  }

  const handleCloseClientAccessDetail = () => {
    setIsDetailDialogOpen(false)
  }

  const handleAddUserAccess = () => {
    // TODO: abrir dialog para dar de alta un acceso (elegir cliente/entidad + tipo de acceso)
  }

  // busca clientes/comerciales por nombre de contacto o email de contacto (POST api/Atlas/Access/Search)
  const handleSearch = async (filters: AtlasAccessFormSearchFilter) => {
    setIsFetchingClients(true)
    await searchAtlasAccess({
      searchText: filters.searchValue.trim(),
      accessType: filters.searchType
    })
      .then(response => {
        if (response.success) {
          setClientsWithAccess(response.data ?? [])
        } else {
          error(t('atlasAccess:notifications.search.error'))
        }
      })
      .finally(() => setIsFetchingClients(false))
  }

  const handleCancelSearch = () => {
    setClientsWithAccess(allClientsWithAccess)
  }

  // ------------------- Super Users Handlers -----------------------------//

  const handleOpenAddSuperUser = () => {
    setIsAddSuperUserDialogOpen(true)
  }

  const handleCloseAddSuperUser = () => {
    setIsAddSuperUserDialogOpen(false)
  }

  // se llama después de añadir/quitar/dar de baja un superusuario, para reflejar el cambio
  const handleSuperUsersActionSuccess = () => {
    fetchSuperUsers()
    fetchKpis()
  }

  const handleRemoveSuperUserPermission = async (row: SuperUserTableModel) => {
    await getConfirmation(ConfirmationDialogs.REMOVE_ATLAS_SUPERUSER_PERMISSION).then(async () => {
      await removeAtlasSuperUserPermission(row.id).then(response => {
        if (response.success) {
          info(t('atlasAccess:components.superUsersCard.notifications.removePermission.success'))
          handleSuperUsersActionSuccess()
        } else {
          error(t('atlasAccess:components.superUsersCard.notifications.removePermission.error'))
        }
      })
    })
    closeConfirmation()
  }

  const handleDeactivateSuperUser = async (row: SuperUserTableModel) => {
    await getConfirmation(ConfirmationDialogs.DEACTIVATE_ATLAS_USER).then(async () => {
      await deactivateAtlasUser(row.id).then(response => {
        if (response.success) {
          info(t('atlasAccess:components.superUsersCard.notifications.deactivate.success'))
          handleSuperUsersActionSuccess()
        } else {
          error(t('atlasAccess:components.superUsersCard.notifications.deactivate.error'))
        }
      })
    })
    closeConfirmation()
  }
  // #endregion Functions Ends

  // #region Form Definition Starts
  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  // #endregion DataTable Config Ends

  // #region Hooks Starts
  const fetchKpis = useCallback(async () => {
    setIsFetching(true)
    await fetchAtlasAccessKpis()
      .then(response => {
        if (response.success) {
          setAtlasAccessKpis(response.data)
        } else {
          error(t('atlasAccess:notifications.kpis.error'))
        }
      })
      .finally(() => setIsFetching(false))
  }, [])

  const fetchTable = useCallback(async () => {
    setIsFetchingClients(true)
    await fetchAtlasAccessTable()
      .then(response => {
        if (response.success) {
          setClientsWithAccess(response.data ?? [])
          setAllClientsWithAccess(response.data ?? [])
        } else {
          error(t('atlasAccess:notifications.table.error'))
        }
      })
      .finally(() => setIsFetchingClients(false))
  }, [])

  const fetchSuperUsers = useCallback(async () => {
    setIsFetchingSuperUsers(true)
    await fetchAtlasSuperUsers()
      .then(response => {
        if (response.success) {
          setSuperUsers(response.data ?? [])
        } else {
          error(t('atlasAccess:components.superUsersCard.notifications.fetch.error'))
        }
      })
      .finally(() => setIsFetchingSuperUsers(false))
  }, [])

  useEffect(() => {
    fetchKpis()
    fetchTable()
    fetchSuperUsers()
  }, [])
  // #endregion Hooks Ends

  // colores distintos por KPI (semanticos del theme) para diferenciarlos de un vistazo
  const accessKpis = [
    {
      key: 'clientsWithAccess',
      description: t('atlasAccess:kpis.clientsWithAccess'),
      icon: faBuilding as IconProp,
      value: atlasAccessKpis?.clientsWithAccessCount,
      color: theme.palette.primary.main
    },
    {
      key: 'commercialsWithAccess',
      description: t('atlasAccess:kpis.commercialsWithAccess'),
      icon: faUserTie as IconProp,
      value: atlasAccessKpis?.commercialsWithAccessCount,
      color: theme.palette.warning.main
    },
    {
      key: 'usersRegistered',
      description: t('atlasAccess:kpis.usersRegistered'),
      icon: faUsers as IconProp,
      value: atlasAccessKpis?.registeredUsersCount,
      color: theme.palette.secondary.main
    },
    {
      key: 'superUsers',
      description: t('atlasAccess:kpis.superUsers'),
      icon: faUserShield as IconProp,
      value: atlasAccessKpis?.superUsersCount,
      color: theme.palette.success.main
    }
  ]

  return (
    <Page layout={PageLayout.MULTI_COLUMN} fullWidth={true}>
      <PageCenterColumn>
        <PageHeader
          title={t('atlasAccess:header.title')}
          subtitle={t('atlasAccess:header.subtitle')}
        />
        <Box
          display="flex"
          flexWrap="wrap"
          justifyContent="center"
          gridGap={16}
          margin={2}
          marginBottom={2}
        >
          {accessKpis.map(kpi => (
            <KpiItem
              key={kpi.key}
              value={kpi.value}
              description={kpi.description}
              icon={kpi.icon}
              color={kpi.color}
              isLoading={isFetching}
            />
          ))}
        </Box>
        <Box display="flex" flexDirection="column">
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="flex-end"
            margin={2}
            marginBottom={1}
          >
            <Chip
              size="small"
              variant={isSearchOpen ? 'default' : 'outlined'}
              icon={<SearchIcon />}
              clickable
              label={t('atlasAccess:components.searchFilterCard.title')}
              color={isSearchOpen ? 'primary' : 'default'}
              onClick={handleToggleSearch}
            />
          </Box>
          <Box display="flex" flexDirection="row" flexGrow={1} minWidth={0}>
            <ClientsAccessTable
              data={clientsWithAccess}
              isLoading={isFetchingClients}
              onRowInsert={handleAddUserAccess}
              onViewDetail={handleViewClientAccessDetail}
            />
            <Box
              style={{
                width: isSearchOpen ? 340 : 0,
                marginLeft: isSearchOpen ? theme.spacing(2) : 0,
                marginRight: isSearchOpen ? theme.spacing(2) : 0,
                marginBottom: theme.spacing(2),
                overflow: 'hidden',
                transition: theme.transitions.create(['width', 'margin-left', 'margin-right'], {
                  duration: 300
                })
              }}
            >
              <Box width={340}>
                <SearchFilterCard onSearch={handleSearch} onCancel={handleCancelSearch} />
              </Box>
            </Box>
          </Box>
          <Box display="flex" flexDirection="row">
            <SuperUsersCard
              data={superUsers}
              isLoading={isFetchingSuperUsers}
              onAdd={handleOpenAddSuperUser}
              onRemovePermission={handleRemoveSuperUserPermission}
              onDeactivate={handleDeactivateSuperUser}
            />
          </Box>
        </Box>

        {/* MAIN BODY COMPONENTS */}
        <ClientDetailAccessDialog
          open={isDetailDialogOpen}
          entity={selectedClientAccess}
          onClose={handleCloseClientAccessDetail}
        />
        <AddSuperUserDialog
          open={isAddSuperUserDialogOpen}
          onClose={handleCloseAddSuperUser}
          onSuccess={handleSuperUsersActionSuccess}
        />
      </PageCenterColumn>
    </Page>
  )
}
